export type SheetSide = 'right' | 'left';

export const DEFAULT_DRAWER_WIDTH = 480;
export const MIN_DRAWER_WIDTH = 380;
export const MAX_DRAWER_WIDTH = 720;
export const DRAWER_KEYBOARD_STEP = 16;

export function clampWidth(
  width: number,
  min: number = MIN_DRAWER_WIDTH,
  max: number = MAX_DRAWER_WIDTH,
): number {
  const lo = Number.isFinite(min) ? min : MIN_DRAWER_WIDTH;
  const hi = Math.max(lo, Number.isFinite(max) ? max : MAX_DRAWER_WIDTH);
  const n = Number(width);
  if (!Number.isFinite(n)) return lo;
  return Math.round(Math.max(lo, Math.min(hi, n)));
}

export function nextWidthFromDrag(
  side: SheetSide,
  startX: number,
  startW: number,
  currentX: number,
  min: number = MIN_DRAWER_WIDTH,
  max: number = MAX_DRAWER_WIDTH,
): number {
  const delta = side === 'right' ? startX - currentX : currentX - startX;
  return clampWidth(startW + delta, min, max);
}

export function readStoredWidth(
  key: string | undefined,
  fallback: number = DEFAULT_DRAWER_WIDTH,
  min: number = MIN_DRAWER_WIDTH,
  max: number = MAX_DRAWER_WIDTH,
): number {
  if (!key) return clampWidth(fallback, min, max);
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return clampWidth(fallback, min, max);
    const parsed = parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return clampWidth(fallback, min, max);
    return clampWidth(parsed, min, max);
  } catch {
    return clampWidth(fallback, min, max);
  }
}

export function writeStoredWidth(
  key: string | undefined,
  width: number,
): void {
  if (!key) return;
  try {
    window.localStorage.setItem(key, String(Math.round(width)));
  } catch {
  }
}

export const MIN_VIEWPORT_DRAWER_WIDTH = 420;
export const MAX_VIEWPORT_DRAWER_FRACTION = 0.9;

export interface DrawerWidthBounds {
  minWidth: number;
  maxWidth: number;
  defaultWidth: number;
}

export function viewportDrawerBounds({
  viewportWidth,
  widthPercent,
  defaultWidthPx,
  minWidthPx = MIN_VIEWPORT_DRAWER_WIDTH,
  maxWidthFraction = MAX_VIEWPORT_DRAWER_FRACTION,
}: {
  viewportWidth: number;
  widthPercent: number;
  defaultWidthPx?: number;
  minWidthPx?: number;
  maxWidthFraction?: number;
}): DrawerWidthBounds {
  const vw = Number.isFinite(viewportWidth) && viewportWidth > 0 ? viewportWidth : 1440;
  const pct = Number.isFinite(widthPercent) && widthPercent > 0 ? widthPercent : 0.5;

  const opening = Number.isFinite(defaultWidthPx) && defaultWidthPx! > 0
    ? Math.round(defaultWidthPx!)
    : Math.round(vw * pct);
  const minWidth = Math.max(1, Math.min(minWidthPx, opening));
  const maxWidth = Math.max(minWidth, Math.round(vw * maxWidthFraction));

  return {
    minWidth,
    maxWidth,
    defaultWidth: clampWidth(opening, minWidth, maxWidth),
  };
}
